import type {
  IDataMartRecord,
  ITargetEntityDmColumnRecord,
  ITargetEntityDmRecord
} from "../../../interfaces/dataMartModelling.interfaces";
import type {
  DataMartColumn,
  DataMartDimension,
  DataMartFact,
  DataMartGraph,
  DataMartModel
} from "./types";

const FACT_IDENTIFIER = /fact/i;
const DIMENSION_IDENTIFIER = /scd|dim/i;

const normalizeIdentifier = (value: string | null | undefined) =>
  value ? value.trim().toLowerCase() : "";

const normalizeDimensionKey = (value: string | boolean | null | undefined) => {
  if (!value || typeof value === "boolean") {
    return null;
  }
  const cleaned = value.replace(/[^A-Za-z0-9_]/g, "").trim().toLowerCase();
  if (!cleaned || cleaned === "self") {
    return null;
  }
  return cleaned;
};

const inferRole = (record: ITargetEntityDmColumnRecord): DataMartColumn["role"] => {
  const rawDimensionKey = record.dimension_key;
  const hasDimensionKey =
    typeof rawDimensionKey === "boolean"
      ? rawDimensionKey
      : Boolean(rawDimensionKey?.trim().toUpperCase() && rawDimensionKey.trim().toUpperCase() !== "SELF");
  if (hasDimensionKey) {
    return "dimension";
  }
  const rawAttribute = record.dimension_attribute;
  const attributeMarker =
    typeof rawAttribute === "string"
      ? rawAttribute.trim().toUpperCase()
      : rawAttribute === true
        ? "Y"
        : "";
  if (attributeMarker === "MEASURE") {
    return "measure";
  }
  if (attributeMarker === "Y") {
    return "attribute";
  }
  return "degenerate";
};

export const buildDataMartColumn = (record: ITargetEntityDmColumnRecord): DataMartColumn => ({
  record,
  normalizedName: normalizeIdentifier(record.target_entity_dm_column_name),
  normalizedDimensionKey: normalizeDimensionKey(record.dimension_key),
  role: inferRole(record)
});

const partitionEntities = (
  records: ITargetEntityDmRecord[]
): { facts: ITargetEntityDmRecord[]; dimensions: ITargetEntityDmRecord[] } => {
  const facts: ITargetEntityDmRecord[] = [];
  const dimensions: ITargetEntityDmRecord[] = [];
  records.forEach((record) => {
    const construct = record.table_construct ?? "";
    const normalizedName = record.target_entity_dm_name ?? "";
    const isFact = FACT_IDENTIFIER.test(construct) || normalizedName.toLowerCase().startsWith("f_");
    const isDimension =
      DIMENSION_IDENTIFIER.test(construct) || normalizedName.toLowerCase().startsWith("d_");
    if (isFact && !isDimension) {
      facts.push(record);
    } else {
      dimensions.push(record);
    }
  });
  return { facts, dimensions };
};

const buildEntityId = (record: ITargetEntityDmRecord) =>
  `${record.target_schema ?? "analytics"}_${record.target_entity_dm_name}`;

const collectColumns = (
  entityId: number | null,
  columnMap: Map<number, ITargetEntityDmColumnRecord[]>
): DataMartColumn[] => {
  if (entityId == null) {
    return [];
  }
  const columns = columnMap.get(entityId) ?? [];
  return columns.map(buildDataMartColumn);
};

const buildFacts = (
  factRecords: ITargetEntityDmRecord[],
  columnMap: Map<number, ITargetEntityDmColumnRecord[]>
): DataMartFact[] =>
  factRecords.map((record) => ({
    id: buildEntityId(record),
    record,
    kind: "fact",
    columns: collectColumns(record.target_entity_dm_id, columnMap),
    dimensionIds: []
  }));

const buildDimensions = (
  dimensionRecords: ITargetEntityDmRecord[],
  columnMap: Map<number, ITargetEntityDmColumnRecord[]>
): DataMartDimension[] =>
  dimensionRecords.map((record) => ({
    id: buildEntityId(record),
    record,
    kind: "dimension",
    columns: collectColumns(record.target_entity_dm_id, columnMap),
    parentDimensionIds: [],
    childDimensionIds: []
  }));

type RelationshipAccumulator = {
  id: string;
  type: "fact-dimension" | "dimension-dimension";
  sourceId: string;
  targetId: string;
  columnNames: Set<string>;
};

const registerRelationship = (
  registry: Map<string, RelationshipAccumulator>,
  type: RelationshipAccumulator["type"],
  sourceId: string,
  targetId: string,
  columnName: string
) => {
  if (!sourceId || !targetId || sourceId === targetId) {
    return;
  }
  const key = `${type}:${sourceId}->${targetId}`;
  const existing = registry.get(key);
  if (existing) {
    existing.columnNames.add(columnName);
    return;
  }
  registry.set(key, {
    id: key,
    type,
    sourceId,
    targetId,
    columnNames: new Set([columnName])
  });
};

const buildGraphsFromRecords = (
  dataMarts: IDataMartRecord[],
  groupedRecords: Map<string, ITargetEntityDmRecord[]>,
  columns: Map<number, ITargetEntityDmColumnRecord[]>
): DataMartGraph[] => {
  const graphs: DataMartGraph[] = [];
  const dataMartLookup = new Map(
    dataMarts.map((record) => [normalizeIdentifier(record.data_mart), record])
  );

  groupedRecords.forEach((records, datamartNameRaw) => {
    const datamartName = normalizeIdentifier(datamartNameRaw);
    const dataMartRecord =
      dataMartLookup.get(datamartName) ??
      ({
        data_mart_id: null,
        data_mart: datamartNameRaw,
        environment_id: records[0]?.environment_id ?? 0,
        customer_id: records[0]?.customer_id ?? 0
      } as IDataMartRecord);

    const { facts: factRecords, dimensions: dimensionRecords } = partitionEntities(records);
    const facts = buildFacts(factRecords, columns);
    const dimensions = buildDimensions(dimensionRecords, columns);
    const dimensionLookup = new Map<string, DataMartDimension>();
    dimensions.forEach((dimension) => {
      const normalizedName = normalizeIdentifier(dimension.record.target_entity_dm_name);
      const normalizedSchemaName = normalizeIdentifier(
        `${dimension.record.target_schema ?? ""}.${dimension.record.target_entity_dm_name}`
      );
      dimensionLookup.set(normalizedName, dimension);
      dimensionLookup.set(normalizedSchemaName, dimension);
    });

    const relationshipRegistry = new Map<string, RelationshipAccumulator>();

    const ensureDimensionRelationship = (
      source: { id: string; columns: DataMartColumn[] },
      type: RelationshipAccumulator["type"]
    ) => {
      source.columns.forEach((column) => {
        if (!column.normalizedDimensionKey) {
          return;
        }
        const target = dimensionLookup.get(column.normalizedDimensionKey);
        if (!target) {
          return;
        }
        registerRelationship(
          relationshipRegistry,
          type,
          source.id,
          target.id,
          column.record.target_entity_dm_column_name
        );
        if (type === "fact-dimension") {
          const fact = facts.find((candidate) => candidate.id === source.id);
          if (fact && !fact.dimensionIds.includes(target.id)) {
            fact.dimensionIds.push(target.id);
          }
        } else if (type === "dimension-dimension") {
          const dimension = dimensions.find((candidate) => candidate.id === source.id);
          if (dimension && !dimension.childDimensionIds.includes(target.id)) {
            dimension.childDimensionIds.push(target.id);
          }
          if (!target.parentDimensionIds.includes(source.id)) {
            target.parentDimensionIds.push(source.id);
          }
        }
      });
    };

    facts.forEach((fact) => ensureDimensionRelationship(fact, "fact-dimension"));
    dimensions.forEach((dimension) => ensureDimensionRelationship(dimension, "dimension-dimension"));

    const relationships = Array.from(relationshipRegistry.values()).map((item) => ({
      id: item.id,
      type: item.type,
      sourceId: item.sourceId,
      targetId: item.targetId,
      columnNames: Array.from(item.columnNames)
    }));

    graphs.push({
      dataMart: dataMartRecord,
      facts,
      dimensions,
      relationships
    });
  });

  return graphs;
};

export type BuildDataMartModelParams = {
  dataMarts: IDataMartRecord[];
  targetEntities: ITargetEntityDmRecord[];
  targetEntityColumns: ITargetEntityDmColumnRecord[];
};

export function buildDataMartModel({
  dataMarts,
  targetEntities,
  targetEntityColumns
}: BuildDataMartModelParams): DataMartModel {
  const groupedRecords = new Map<string, ITargetEntityDmRecord[]>();
  targetEntities.forEach((record) => {
    const normalizedName = normalizeIdentifier(record.datamart);
    const existing = groupedRecords.get(normalizedName) ?? [];
    existing.push(record);
    groupedRecords.set(normalizedName, existing);
  });

  const columnMap = new Map<number, ITargetEntityDmColumnRecord[]>();
  targetEntityColumns.forEach((column) => {
    if (column.target_entity_dm_id == null) {
      return;
    }
    const existing = columnMap.get(column.target_entity_dm_id) ?? [];
    existing.push(column);
    columnMap.set(column.target_entity_dm_id, existing);
  });

  return {
    graphs: buildGraphsFromRecords(dataMarts, groupedRecords, columnMap)
  };
}
